import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import { format } from "date-fns";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMeeting } from "../../../hooks/useMeeting";
import { actionShowSpinner } from "../../../redux-flow/action/Wrapper/Spinner";
import {
  getMeetingInvite,
  sendMeetingInvite,
} from "../../../services/meeting.service";
import {
  getDateFormats,
  getDateTimeLocale,
} from "../../../utils/get-date-time-locale";
import DialogCustom from "../../Common/DialogCustom";
import Textarea from "../../Common/Textarea";
import ToastMessage from "../ToastMessage";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "1vh 1vw",
  },
  button: {
    ...theme.typography.subtitle1,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    minWidth: 180,
    minHeight: 50,
    borderRadius: 25,
    marginTop: "5vh",
  },
  spacer: {
    marginBottom: 20,
  },
  success: {
    backgroundColor: theme.palette.success.dark,
    borderRadius: 5,
    height: 50,
  },
  label: {
    ...theme.typography.subtitle1,
    color: theme.palette.primary.contrastText,
  },
  icon: {
    color: theme.palette.primary.contrastText,
    fontSize: 30,
    marginRight: 50,
  },
  titleModal: {
    color: "#031795",
    fontWeight: "bold",
    fontSize: "20px",
  },
  lgpdInfoContainer: {
    display: "flex",
    flex: 1,
    alignItems: "flex-start",
    justifyContent: "flex-start",
    marginTop: "16px",
  },
}));

const SendInviteModal = ({ setModalSendEmail, meetingInfo, onSuccess, t }) => {
  const classes = useStyles();
  const { meetingInfo: meetingById } = useMeeting({
    spinner: actionShowSpinner,
  });
  const { i18n } = useTranslation();

  const [toastMessage, setToastMessage] = useState({
    message: "",
    type: "error",
    open: false,
  });
  const [emails, setEmails] = useState("");
  const [meetLocation, setMeetLocation] = useState("");
  const [filesAnexo, setFilesAnexo] = useState([]);
  const [content, setContent] = useState();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (meetingInfo?.id) {
      getMeetingInvite(meetingInfo.id).then((response) => {
        setEmails(response.data.attendees);
        setMeetLocation(response.data.locationName);
        setOnlineMeet(response.data.onlineMeetingProvider);
      });
    }
  }, [meetingInfo]);

  useEffect(() => {
    setEmails(meetingById.attendees || "");
    setMeetLocation(meetingById.locationName || "");
    setOnlineMeet(meetingById.onlineMeetingProvider);
  }, [meetingById]);

  const sendEmail = () => {
    setLoading(true);

    if (emails.length <= 0) {
      setToastMessage({
        message: t("meeting.AttentesRequerid"),
        type: "error",
        open: true,
      });
      return;
    }

    const formattedRecipients = emails
      ?.split(";")
      ?.map((email) => ({
        address: email.replaceAll(" ", ""),
      }))
      .filter((e) => e.address !== "");

    const emailValidator =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    let isEmailsValid = true;

    formattedRecipients.forEach((email) => {
      if (!emailValidator.test(String(email.address).toLowerCase())) {
        isEmailsValid = false;
      }
    });

    if (!isEmailsValid) {
      setToastMessage({
        open: true,
        message: "Email inválido.",
        type: "error",
      });

      return;
    }

    const formData = new FormData();

    formattedRecipients.forEach((email, index) => {
      formData.append(`Recipients[${index}].Address`, email.address);
    });

    let count = 0;

    for (const file of filesAnexo) {
      formData.append(`Attachments[${count}].fromFiles`, file.file);
      formData.append(`Attachments[${count}].fileType`, file.contentType);

      count += 1;
    }

    if (filesAnexo.length < 1) {
      formData.append(`Attachments`, []);
    }

    const formattedDate = new Date(meetingInfo.meetDate).toISOString().slice(0, 10).replace(/-/g, "/");
    formData.append("meetId", meetingInfo.id);
    formData.append("meetDate", formattedDate);
    formData.append("OnlineMeetingProvider", onlineMeet);
    formData.append(
      "subject",
      `${meetingInfo?.committeName.props.children[0]} - ${format(
        new Date(meetingInfo?.meetDate),
        getDateFormats(i18n.language),
        {
          locale: getDateTimeLocale(i18n.language),
        }
      )} - ${meetingInfo?.meetStartTime} - ${meetingInfo?.meetEndTime}`
    );
    formData.append("content", content || "");
    formData.append("ccRecipients", []);

    sendMeetingInvite(formData)
      .then((res) => {
        if (res.error.length > 0) {
          setToastMessage({
            message: t("meeting.ErrorMetting"),
            type: "error",
            open: true,
          });

          return;
        }

        onSuccess();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCloseUploadSameFileDialog = () => {
    setSameFilesUploaded([]);
    setOpenUploadSameFileDialog(false);
  };

  const handleReplaceSameFile = () => {
    if (sameFilesUploaded.length > 0) {
      const fileIdssWithSameName = sameFilesUploaded.map(
        (sameFile) => sameFile.id
      );
      const newFilesAnexoList = filesAnexo.filter(
        (file) => !fileIdssWithSameName.includes(file.id)
      );

      setFilesAnexo([...newFilesAnexoList, ...filesUploadedWithSameNameState]);
      setSameFilesUploaded([]);
      setFilesUploadedWithSameName([]);
    }

    setOpenUploadSameFileDialog(false);
  };

  const [sameFilesUploaded, setSameFilesUploaded] = useState([]);
  const [filesUploadedWithSameNameState, setFilesUploadedWithSameName] =
    useState([]);
  const [openUploadSameFileDialog, setOpenUploadSameFileDialog] =
    useState(false);
  const [onlineMeet, setOnlineMeet] = useState(false);

  return (
    <div>
      <DialogCustom
        open={openUploadSameFileDialog}
        handleClose={handleCloseUploadSameFileDialog}
        handleChange={handleReplaceSameFile}
        title={t(`forms.dialog.alert`)}
        body={t("forms.dialog.uploadSameFile")}
        confirmText={t(`forms.dialog.yes`)}
        cancelText={t(`forms.dialog.no`)}
        alert={true}
      />
      <span
        style={{ float: "right", cursor: "pointer" }}
        onClick={() => {
          setModalSendEmail(false);
        }}
      >
        [X] {t("meeting.Close")}{" "}
      </span>
      <span className={classes.titleModal}>
        {meetingInfo?.committeName.props.children[0]} -{" "}
        {format(
          new Date(meetingInfo?.meetDate),
          getDateFormats(i18n.language),
          {
            locale: getDateTimeLocale(i18n.language),
          }
        )}{" "}
        - {meetingInfo?.meetStartTime} {t("meeting.as")}{" "}
        {meetingInfo?.meetEndTime}
      </span>
      <br />
      <br />
      <div className={classes.root}>
        <Grid container>
          <Grid item xs={12}>
            <Textarea
              name="for"
              id="for"
              label="Para"
              value={emails}
              error={emails ? false : true}
              onChange={(event) => {
                setEmails(event.target.value);
              }}
            />
          </Grid>

          <Grid item xs={12} style={{ marginTop: "1%", width: "100%" }}>
            <TextField
              name="subject"
              id="subject"
              label="Assunto"
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              autoComplete="nope"
              disabled={true}
              value={`${meetingInfo?.committeName.props.children[0]} - ${format(
                new Date(meetingInfo?.meetDate),
                getDateFormats(i18n.language),
                {
                  locale: getDateTimeLocale(i18n.language),
                }
              )} - ${meetingInfo?.meetStartTime} ${t("timePeriodSeparator")} ${
                meetingInfo?.meetEndTime
              }`}
            />
          </Grid>
        </Grid>
        <Grid container spacing={3} style={{ display: "flex" }}>
          {!onlineMeet && (
            <Grid item xs={6} style={{ marginTop: "1%" }}>
              <TextField
                name="place"
                id="place"
                autoCompdlete="nope"
                fullWidth
                label="Local"
                value={meetLocation}
                onChange={(event) => {
                  setMeetLocation(event.target.value);
                }}
              />
            </Grid>
          )}
          <Grid item xs={6} style={{ marginTop: "1%" }}>
            <FormControlLabel
              fullWidth
              control={
                <Checkbox
                  value={onlineMeet}
                  checked={onlineMeet}
                  onChange={() => setOnlineMeet(!onlineMeet)}
                  name="onlineMeet"
                  id="onlineMeet"
                  color="primary"
                />
              }
              label={t("meeting.onlineMeet")}
            />
          </Grid>
        </Grid>
        <br />
        <Grid container spacing={3} xs={12} style={{ width: "100%" }}>
          <Grid
            item
            lg={4}
            xs={12}
            style={{ width: "50%", display: "inline-block" }}
          >
            <Typography style={{ textAlign: "left" }}>
              {" "}
              {t("meeting.startDate")}
            </Typography>
            <TextField
              name="startDate"
              id="startDate"
              required
              autoComplete="nope"
              fullWidth
              disabled={true}
              value={
                format(
                  new Date(meetingInfo?.meetDate),
                  getDateFormats(i18n.language),
                  {
                    locale: getDateTimeLocale(i18n.language),
                  }
                ) || ""
              }
            />
            <Typography style={{ textAlign: "left", marginTop: "16px" }}>
              {t("meeting.endDateSendEmail")}{" "}
            </Typography>
            <TextField
              className="endDate"
              name="endDate"
              id="endDate"
              required
              autoComplete="nope"
              fullWidth
              disabled={true}
              value={
                format(
                  new Date(meetingInfo?.meetDate),
                  getDateFormats(i18n.language),
                  {
                    locale: getDateTimeLocale(i18n.language),
                  }
                ) || ""
              }
            />
          </Grid>
          <Grid
            item
            lg={4}
            xs={12}
            style={{ width: "50%", display: "inline-block" }}
          >
            <Typography style={{ textAlign: "left" }}>
              {" "}
              {t("meeting.startTime")}
            </Typography>
            <TextField
              name="startTime"
              id="startTime"
              required
              autoComplete="nope"
              type="time"
              fullWidth
              disabled={true}
              value={meetingInfo?.meetStartTime || ""}
            />
            <Typography style={{ textAlign: "left", marginTop: "16px" }}>
              {" "}
              {t("meeting.endTime")}
            </Typography>
            <TextField
              name="endTime"
              id="endTime"
              required
              autoComplete="nope"
              type="time"
              fullWidth
              disabled={true}
              value={meetingInfo?.meetEndTime || ""}
            />
          </Grid>
        </Grid>

        <TextField
          style={{ width: "100%", marginTop: "1%" }}
          placeholder={t("meeting.messageSendEmail")}
          multiline
          rowsMin={5}
          maxRows={5}
          value={content}
          onChange={(event, newValue) => {
            setContent(event.target.value);
          }}
        />

        <Grid
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginTop: "16px",
          }}
        >
          <Button
            disabled={loading}
            style={{ width: "20%" }}
            color="primary"
            variant="contained"
            onClick={() => sendEmail()}
          >
            {loading ? <CircularProgress size={24} /> : t("meeting.Send")}
          </Button>
        </Grid>
      </div>

      <ToastMessage
        message={toastMessage.message}
        type={toastMessage.type || "error"}
        open={toastMessage.open}
        close={() => setToastMessage({ ...toastMessage, open: false })}
      />
    </div>
  );
};

export default SendInviteModal;
